import { useEffect, useLayoutEffect, useMemo, useState } from 'react';

import { MAX_TIMEOUT_DELAY_MS } from '../constants/timers.js';
import { resolveDeviceAlertExpiryMs, selectLiveDeviceAlerts } from '../lib/device-alerts.js';
import type { DeviceAlert } from '../types';

export const useLiveDeviceAlerts = (alerts: DeviceAlert[]): DeviceAlert[] => {
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Before paint: nowMs can be far behind a cache update, and an alert that arrives already expired
  // would otherwise be painted until the zero-delay timer fires.
  useLayoutEffect(() => {
    setNowMs(Date.now());
  }, [alerts]);

  const liveAlerts = useMemo(() => selectLiveDeviceAlerts(alerts, nowMs), [alerts, nowMs]);

  // A number, so the effect is stable across the fresh array identity every filter produces.
  const expiries = liveAlerts
    .map(resolveDeviceAlertExpiryMs)
    .filter((expiryMs): expiryMs is number => expiryMs !== null);
  const nextExpiryMs = expiries.length ? Math.min(...expiries) : null;

  // nowMs re-arms the timer when a capped delay fires short of the expiry.
  useEffect(() => {
    if (nextExpiryMs === null) {
      return;
    }

    const delayMs = Math.min(Math.max(0, nextExpiryMs - Date.now()), MAX_TIMEOUT_DELAY_MS);
    const expiryTimer = setTimeout(() => setNowMs(Date.now()), delayMs);

    return () => clearTimeout(expiryTimer);
  }, [nextExpiryMs, nowMs]);

  return liveAlerts;
};
